import { useState } from "react";

import { toast } from "@/components/core/toaster";
import { useIsUnitAdmin } from "@/hooks/use-is-unit-admin";
import { useOrgProfile } from "@/hooks/use-org-profile";
import { useUnitDetails } from "@/hooks/use-unit-details";
import { useSite } from "@/hooks/use-site";
import { useSiteTemplates } from "@/hooks/use-site-templates";
import { usePages } from "@/hooks/use-page-editor";
import { usePublishScan } from "@/hooks/use-publish-scan";
import { visibilityForPage, type PrivacyTier } from "@/lib/onboarding/privacy-tiers";
import type { SiteGoal } from "@/components/org-settings/types";
import type { SiteThemePreset } from "@/lib/site-styles/theme-presets";
import type { SiteTemplate } from "@/lib/templates/types";
import type { Site } from "@/types/database";

export interface OnboardingPublishArgs {
  template: SiteTemplate;
  unitIdentifier: string;
  councilName: string;
  districtName: string;
  charteredOrgName: string;
  goal: SiteGoal;
  themePreset: SiteThemePreset;
  privacyTier: PrivacyTier;
}

// The Review & Publish step's orchestration, pulled out of the wizard
// shell since it's the one genuinely complex piece: apply the template,
// set the chosen theme, assign each new page's visibility per the chosen
// privacy tier, then actually publish each page — through the same
// youth-PII preflight every other publish goes through (see
// use-publish-scan.ts), not a special-cased bypass. Today's inline
// TemplateGallery only ever created draft pages; this is also what
// closes that gap and makes "immediate publish" (spec section 4, Step 7)
// real.
export function useOnboardingPublish(site: Site, unitName: string) {
  const { profile, save: saveProfile } = useOrgProfile();
  const { save: saveUnitDetails } = useUnitDetails();
  const { isAdmin } = useIsUnitAdmin();
  const { save: saveSite } = useSite();
  const { applyTemplate } = useSiteTemplates();
  const { updatePage, refetch } = usePages(site.id);
  const { runScan } = usePublishScan();
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publish = async (args: OnboardingPublishArgs): Promise<boolean> => {
    setPublishing(true);
    setError(null);
    try {
      // Unit identity (number/council/district/chartered-org) is the shared
      // unit_details record's to own, not org_profiles'. Only admins can write
      // it (its RLS), and creating a fresh row needs a unit_type that may not
      // be set yet — so this is best-effort: a failure here must never block
      // publishing the site, only warn. Skipped entirely when there's nothing
      // to save or the user can't write it.
      const hasIdentity = Boolean(
        args.unitIdentifier || args.councilName || args.districtName || args.charteredOrgName
      );
      if (hasIdentity && isAdmin) {
        try {
          await saveUnitDetails(
            {
              unit_number: args.unitIdentifier || null,
              council_name: args.councilName || null,
              district_name: args.districtName || null,
              chartered_org_name: args.charteredOrgName || null,
            },
            profile?.unit_type ?? null
          );
        } catch (identityError) {
          toast.error(
            identityError instanceof Error && identityError.message
              ? identityError.message
              : "We couldn't save your unit details, but your site was still published."
          );
        }
      }
      await saveProfile({
        site_goal: args.goal,
        onboarding_completed_at: new Date().toISOString(),
      });
      await saveSite({ theme_config: args.themePreset.config });
      await applyTemplate({
        siteId: site.id,
        template: args.template,
        unitName,
        unitIdentifier: args.unitIdentifier,
      });

      const { data: createdPages } = await refetch();
      let blockedCount = 0;

      for (const page of createdPages ?? []) {
        const visibility = visibilityForPage(args.privacyTier, page.slug);
        const scanResult = await runScan(page);
        if (scanResult.blocking.length > 0) {
          blockedCount += 1;
          if (visibility !== page.visibility) await updatePage(page.id, { visibility });
          continue;
        }
        await updatePage(page.id, { visibility, is_published: true });
      }

      if (blockedCount > 0) {
        setError(
          `${blockedCount} page${blockedCount === 1 ? "" : "s"} couldn't be published automatically and ${blockedCount === 1 ? "needs" : "need"} a quick review in Studio — the rest of your site is live.`
        );
      }
      return true;
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Something went wrong publishing your site.");
      return false;
    } finally {
      setPublishing(false);
    }
  };

  return { publish, publishing, error };
}
